import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const dataParallelComponent: ComponentDef = {
  id: 'data-parallel',
  name: 'Data Parallel',
  description: 'Replicate the model across GPUs and average gradients with AllReduce.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 4, min: 2, max: 1024, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'naive-dp',
      options: [
        { label: 'Naive DP', value: 'naive-dp' },
        { label: 'ZeRO Stage 1', value: 'zero-1' },
        { label: 'ZeRO Stage 2', value: 'zero-2' },
        { label: 'ZeRO Stage 3 (FSDP)', value: 'zero-3' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_gpus = Number(params.n_gpus);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('batch', [d_model]);

    if (variant === 'naive-dp') {
      // Naive DP: replicate model, compute, AllReduce gradients
      g.replicate('replicas', 'batch', [d_model], n_gpus);
      g.linear('forward', 'replicas', [d_model, d_model]);
      g.allreduce('avg_grad', 'forward', [d_model, d_model]);
      g.scale('optimizer', 'avg_grad', 'Optimizer (full)');
      g.output('out', 'optimizer');
    } else if (variant === 'zero-1') {
      // ZeRO Stage 1: shard optimizer states, full params & gradients
      g.replicate('replicas', 'batch', [d_model], n_gpus);
      g.linear('forward', 'replicas', [d_model, d_model]);
      g.allreduce('avg_grad', 'forward', [d_model, d_model]);
      g.split('shard_opt', 'avg_grad', [d_model / n_gpus], 'Shard optimizer states');
      g.scale('optimizer', 'shard_opt', `Optimizer (1/${n_gpus})`);
      g.allgather('gather_params', 'optimizer', [d_model]);
      g.output('out', 'gather_params');
    } else if (variant === 'zero-2') {
      // ZeRO Stage 2: shard gradients + optimizer states
      g.replicate('replicas', 'batch', [d_model], n_gpus);
      g.linear('forward', 'replicas', [d_model, d_model]);
      g.reduceScatter('shard_grad', 'forward', [d_model / n_gpus]);
      g.scale('optimizer', 'shard_grad', `Optimizer (1/${n_gpus})`);
      g.allgather('gather_params', 'optimizer', [d_model]);
      g.output('out', 'gather_params');
    } else {
      // ZeRO Stage 3 (FSDP): shard everything — params, gradients, optimizer
      g.split('shard_params', 'batch', [d_model / n_gpus], `/${n_gpus}`);
      g.allgather('full_params', 'shard_params', [d_model]);
      g.linear('forward', 'full_params', [d_model, d_model]);
      g.reduceScatter('shard_grad', 'forward', [d_model / n_gpus]);
      g.scale('optimizer', 'shard_grad', `Optimizer (1/${n_gpus})`);
      g.output('out', 'optimizer');
    }

    return g.build();
  }
};
